import styled from "@emotion/styled";
import { useState } from "react";
import { useDealer } from "src/hooks/useDealer";
import { useSendConditionFiles } from "src/hooks/useSendConditionFiles";
import { useApplication } from "../ApplicationContext";
import {
  UserAccountIcon2,
  UserAccountSelectedIcon2,
  BankIcon,
  BankSelectedIcon,
  Agreement03,
} from "src/assets/icons";
import ConditionsDialog from "src/components/Conditions/ConditionsDialog";
import UploadDocumentStep from "src/components/Conditions/UploadDocumentStep";
import SendToStep from "src/components/Conditions/SendToStep";
import ListMenuItem from "./ListMenuItem";

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 24px 0px;
  gap: 6px;
`;

export default function ListMenu({
  currentSection,
}: {
  currentSection: string;
}) {
  const { dealer_id } = useDealer();
  const { sendConditionFiles } = useSendConditionFiles();
  const {
    application: { application_id, los_id, lenders = [] },
  } = useApplication();
  const [isConditionsDialogOpen, setIsConditionsDialogOpen] = useState(false);

  // Filter to only enabled lenders
  const enabledLenders = lenders.filter((lender) => lender.enabled);

  return (
    <ListContainer>
      <ListMenuItem
        id="overview"
        label="Overview"
        subLabel="Update key dealership information."
        icon={UserAccountIcon2}
        selectedIcon={UserAccountSelectedIcon2}
        path={`/${dealer_id}/applications/${application_id}`}
        currentSection={currentSection}
      />
      <ListMenuItem
        id="lenders"
        label="Lenders"
        subLabel="Manage dealer lenders and defaults."
        icon={BankIcon}
        selectedIcon={BankSelectedIcon}
        path={`/${dealer_id}/applications/${application_id}/lenders`}
        currentSection={currentSection}
      />
      <ListMenuItem
        id="conditions"
        label="Conditions"
        subLabel="Review and manage conditions."
        icon={Agreement03}
        selectedIcon={Agreement03}
        currentSection={currentSection}
        onClick={() => setIsConditionsDialogOpen(true)}
        disabled={!los_id}
      />
      {/*
      Temporarily hiding documents menu item.
      Ticket: https://porchpass.atlassian.net/browse/DEA-513
      <ListMenuItem
        id="documents"
        label="Documents"
        subLabel="Manage application files."
        icon={FolderIcon}
        selectedIcon={FolderSelectedIcon}
        path={`/${dealer_id}/applications/${application_id}/documents`}
        currentSection={currentSection}
      /> */}

      {isConditionsDialogOpen && (
        <div>
          <ConditionsDialog
            open={isConditionsDialogOpen}
            onOpenChange={setIsConditionsDialogOpen}
            lenders={enabledLenders}
            applicationId={application_id}
            onConfirm={async (
              documents: File[],
              selectedLenders,
              conditionFiles,
              conditions,
              newConditions,
            ) => {
              if (!application_id) {
                throw new Error("Application ID is required");
              }

              // For condition-based upload (only porchpass):
              // Send all files (existing conditions + new conditions) to DigiFi
              if (
                (conditionFiles?.size || 0) > 0 ||
                (newConditions?.length || 0) > 0
              ) {
                if (!conditions) {
                  throw new Error("Conditions not available");
                }

                if (!los_id) {
                  throw new Error(
                    "The application is not yet in Digifi. Please try again later or contact support.",
                  );
                }

                const filesMap = conditionFiles || new Map<string, File>();
                const result = await sendConditionFiles(
                  filesMap,
                  conditions,
                  application_id,
                  newConditions,
                  los_id,
                );

                if (!result.success) {
                  throw new Error(
                    result.error || "Failed to upload condition files",
                  );
                }
              }

              // For bulk upload (multiple lenders):
              // TODO: Implement bulk upload logic for multiple lenders
              // This would require a separate endpoint for bulk document uploads
              if (documents.length > 0) {
                console.log("Bulk upload for documents:", documents);
                console.log("Selected lenders:", selectedLenders);
              }
            }}
          >
            <SendToStep />
            <UploadDocumentStep />
          </ConditionsDialog>
        </div>
      )}
    </ListContainer>
  );
}
